import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, MessageCircle, BookOpen, FileQuestion, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import AppFooter from '../components/AppFooter';

const SUPPORT_EMAIL = 'support@selahslate.com';
const RESPONSE_WINDOW = 'within 2 business days';

const FAQS = [
  {
    q: 'Is Selah Slate really free?',
    a: "Yes. Forever. We don't sell ads, we don't charge subscriptions, and we don't sell your data. Selah Slate is funded by Selah and Stone Legacy Holdings LLC as a ministry. The only thing we ask is that you tell someone else who's struggling.",
  },
  {
    q: 'Are you a credit repair company?',
    a: "No. Selah Slate is an educational tool that helps you draft your own FCRA Section 611 dispute letters using your own factual information. We never mail letters for you, negotiate with creditors, or contact bureaus on your behalf. You stay in control.",
  },
  {
    q: 'How do I upload my credit report?',
    a: 'Get your free credit report at AnnualCreditReport.com (the only federally authorized site). Download the PDF, then on the Selah Slate dashboard click "Upload Credit Report." We analyze the PDF, extract negative items, and immediately delete the raw text from our servers.',
  },
  {
    q: 'Why are disputes capped at 5 per round?',
    a: "FCRA Section 611(a)(3)(A) allows bureaus to dismiss disputes as \"frivolous\" if you submit too many at once. Industry best practice is 5 or fewer per round. We enforce this cap to protect your dispute from being thrown out.",
  },
  {
    q: 'What happens after I mail my letters?',
    a: 'Bureaus have 30 days (35 with mail transit) to respond under FCRA. Selah Slate starts a countdown timer for each bureau. When the response arrives, log it on the Disputes page — we\'ll generate Method of Verification (MOV) letters or CFPB escalation guidance automatically based on the outcome.',
  },
  {
    q: 'Can I delete my account and data?',
    a: 'Yes. Settings → "Delete my account" wipes your profile, uploads, analyses, and disputes immediately. We retain no backups.',
  },
  {
    q: 'Will Selah Slate guarantee my credit score will go up?',
    a: "No tool can honestly guarantee a specific score outcome — anyone who promises that is lying. What we can do is help you remove inaccurate, incomplete, or unverifiable negative items, which often results in score improvement.",
  },
  {
    q: 'My dispute draft says it expires in 7 days. Why?',
    a: "We keep storage lean for everyone by automatically clearing unfinished drafts after 7 days. You'll receive an email at day 5 reminding you to finish. Saving the draft again refreshes the 7-day window.",
  },
  {
    q: 'Does Selah Slate work offline?',
    a: 'Once installed as a PWA (Add to Home Screen on iOS, Install button on Android/Chrome), the app shell loads offline. You\'ll need an internet connection to upload reports, run AI analysis, and submit disputes — but you can review existing data offline.',
  },
];

export default function SupportPage() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="support-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="home-link">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </Link>
            <Link
              to="/dashboard"
              className="text-sm text-[#36454F]/70 hover:text-[#36454F] font-['Manrope'] flex items-center"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* No animation on the hero — must be visible the moment the page loads
            for Apple App Review (Guideline 1.5 — Support URL). */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-wider text-[#B76E79] font-['Manrope'] font-medium mb-2">
            Help &amp; Support
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl font-semibold text-[#36454F]">
            We're here to help
          </h1>
          <p className="mt-3 text-[#36454F]/70 font-['Manrope'] leading-relaxed">
            Real humans answer every message. No bots, no ticket-system runaround.
            Most questions are answered {RESPONSE_WINDOW}.
          </p>
        </div>

        {/* Contact card — also non-animated so it's instantly visible */}
        <Card className="mb-8 border-[#B76E79]/30 bg-gradient-to-br from-[#E0BFB8]/20 to-white" data-testid="contact-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-[#B76E79]/15 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6 text-[#B76E79]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-1">
                  Email us directly
                </h2>
                <p className="text-sm text-[#36454F]/70 font-['Manrope']">
                  Account help, dispute questions, bug reports, prayer requests &mdash; anything.
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}?subject=Selah%20Slate%20Support`}
                  className="inline-block mt-1 text-sm text-[#B76E79] underline font-['Manrope']"
                  data-testid="email-support-plain-link"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Selah%20Slate%20Support`} data-testid="email-support-link">
                <Button className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm whitespace-nowrap">
                  <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Email Support
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="mb-8 animate-fade-in-up animation-delay-200">
          <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4 flex items-center gap-2">
            <FileQuestion className="w-6 h-6 text-[#B76E79]" strokeWidth={1.5} />
            Frequently asked
          </h2>
          <div className="space-y-2">
            {FAQS.map((faq, i) => {
              const open = openIndex === i;
              return (
                <Card
                  key={i}
                  className="border-[#36454F]/10 bg-white overflow-hidden"
                  data-testid={`faq-item-${i}`}
                >
                  <button
                    onClick={() => setOpenIndex(open ? null : i)}
                    className="w-full p-4 sm:p-5 text-left flex items-start justify-between gap-4 hover:bg-[#F5F5F5]/50 transition-colors"
                    aria-expanded={open}
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-['Manrope'] font-medium text-[#36454F] text-sm sm:text-base">
                      {faq.q}
                    </span>
                    <span
                      className={`text-[#B76E79] font-['Manrope'] text-xl leading-none flex-shrink-0 transition-transform ${open ? 'rotate-45' : ''}`}
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </button>
                  {open && (
                    <div className="px-4 sm:px-5 pb-5 pt-0 -mt-1">
                      <p className="text-sm text-[#36454F]/75 font-['Manrope'] leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Resources */}
        <div className="mb-8 animate-fade-in-up animation-delay-300">
          <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-4 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#B76E79]" strokeWidth={1.5} />
            More resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/privacy"
              className="block p-4 rounded-sm bg-white border border-[#36454F]/10 hover:border-[#B76E79]/40 transition-colors"
              data-testid="link-privacy"
            >
              <p className="font-['Manrope'] font-medium text-[#36454F] text-sm mb-0.5">
                Privacy Policy
              </p>
              <p className="text-xs text-[#36454F]/60 font-['Manrope']">
                What we collect and why
              </p>
            </Link>
            <Link
              to="/terms"
              className="block p-4 rounded-sm bg-white border border-[#36454F]/10 hover:border-[#B76E79]/40 transition-colors"
              data-testid="link-terms"
            >
              <p className="font-['Manrope'] font-medium text-[#36454F] text-sm mb-0.5">
                Terms of Service
              </p>
              <p className="text-xs text-[#36454F]/60 font-['Manrope']">
                The legal agreement
              </p>
            </Link>
            <Link
              to="/disclosures"
              className="block p-4 rounded-sm bg-white border border-[#36454F]/10 hover:border-[#B76E79]/40 transition-colors"
              data-testid="link-disclosures"
            >
              <p className="font-['Manrope'] font-medium text-[#36454F] text-sm mb-0.5">
                Mandatory Disclosures
              </p>
              <p className="text-xs text-[#36454F]/60 font-['Manrope']">
                FCRA rights, CFPB how-tos
              </p>
            </Link>
            <a
              href="https://www.consumerfinance.gov/complaint/"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-4 rounded-sm bg-white border border-[#36454F]/10 hover:border-[#B76E79]/40 transition-colors"
              data-testid="link-cfpb"
            >
              <p className="font-['Manrope'] font-medium text-[#36454F] text-sm mb-0.5 flex items-center gap-1">
                File a CFPB complaint
                <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
              </p>
              <p className="text-xs text-[#36454F]/60 font-['Manrope']">
                If a bureau won't comply
              </p>
            </a>
          </div>
        </div>

        <p className="text-xs text-[#36454F]/45 font-['Manrope'] text-center mt-12">
          Selah Slate is provided by Selah and Stone Legacy Holdings LLC.
          Educational tool — not legal or financial advice.
        </p>
      </main>

      <AppFooter />
    </div>
  );
}
